import { useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "@/config";
import { Wrench, MapPin, CheckCircle, Clock, AlertCircle } from "lucide-react";

interface RepairOrder {
    ro_id: number;
    status: string;
    total_amount: string | null;
    vehicle_vin: string | null;
    vehicle_desc: string | null;
    created_at: string;
    shop: { shop_name: string; city: string | null; state: string | null; phone: string | null };
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof Clock }> = {
    OPEN:        { label: "Open",        color: "text-blue-600 bg-blue-50",   icon: Clock        },
    IN_PROGRESS: { label: "In Progress", color: "text-orange-600 bg-orange-50", icon: Wrench     },
    COMPLETED:   { label: "Completed",   color: "text-green-600 bg-green-50", icon: CheckCircle  },
    INVOICED:    { label: "Invoice Ready", color: "text-violet-600 bg-violet-50", icon: AlertCircle },
    PAID:        { label: "Paid",        color: "text-gray-600 bg-gray-50",   icon: CheckCircle  },
};

export default function CustomerRepairOrdersPage() {
    const [orders, setOrders] = useState<RepairOrder[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API_URL}/repair-shops/customer/mine`, { withCredentials: true })
            .then((r) => setOrders(r.data.repair_orders ?? []))
            .catch(() => setOrders([]))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <p className="p-8 text-center text-muted-foreground">Loading repair orders…</p>;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">My Repair Orders</h1>
                <p className="text-sm text-muted-foreground mt-1">Track your vehicle service history and pay outstanding invoices.</p>
            </div>

            {orders.length === 0 ? (
                <div className="bg-card rounded-xl border p-10 text-center">
                    <Wrench className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No repair orders found.</p>
                    <a
                        href="/repair-shops"
                        className="inline-block mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700 transition"
                    >
                        Find a Shop
                    </a>
                </div>
            ) : (
                <div className="space-y-4">
                    {orders.map((ro) => {
                        const cfg = STATUS_CONFIG[ro.status] ?? { label: ro.status, color: "text-gray-600 bg-gray-50", icon: Clock };
                        const Icon = cfg.icon;
                        return (
                            <div key={ro.ro_id} className="bg-card rounded-xl border p-5">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="space-y-1">
                                        <p className="font-semibold">{ro.shop.shop_name}</p>
                                        {(ro.shop.city || ro.shop.state) && (
                                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                <MapPin className="h-3 w-3" />
                                                {[ro.shop.city, ro.shop.state].filter(Boolean).join(", ")}
                                            </p>
                                        )}
                                        {ro.vehicle_desc && (
                                            <p className="text-xs text-muted-foreground">{ro.vehicle_desc}</p>
                                        )}
                                        <p className="text-xs text-muted-foreground">
                                            RO #{ro.ro_id} · {new Date(ro.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.color}`}>
                                            <Icon className="h-3 w-3" />
                                            {cfg.label}
                                        </span>
                                        {ro.total_amount && (
                                            <p className="text-base font-bold">${ro.total_amount}</p>
                                        )}
                                    </div>
                                </div>

                                {ro.status === "INVOICED" && (
                                    <div className="mt-4 pt-4 border-t">
                                        <button
                                            onClick={async () => {
                                                await axios.post(
                                                    `${API_URL}/repair-shops/repair-orders/${ro.ro_id}/pay`,
                                                    {},
                                                    { withCredentials: true }
                                                );
                                                setOrders((prev) =>
                                                    prev.map((o) => o.ro_id === ro.ro_id ? { ...o, status: "PAID" } : o)
                                                );
                                            }}
                                            className="rounded-lg bg-green-600 px-4 py-2 text-sm text-white font-medium hover:bg-green-700 transition"
                                        >
                                            Pay Invoice
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
